import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export default function HeroSection() {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.play().catch(() => {
      // autoplay may be blocked until user interaction
    });
  }, []);

  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ backgroundColor: "#0d0b1e" }}>
      {/* Full-screen background video */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        src="https://storage.helloreaddy.io/project_files/9d47c388-655f-4be5-bf40-a8a867a3d101/dff432d6-18fe-4ee4-b85f-6cf29c16a822_version-4.mp4"
        autoPlay
        muted
        loop
        playsInline
        style={{ zIndex: 1 }}
      />

      {/* Dark overlay — keeps text readable */}
      <div
        className="absolute inset-0 z-[2]"
        style={{
          background: "linear-gradient(135deg, rgba(13,11,30,0.85) 0%, rgba(13,11,30,0.60) 50%, rgba(13,11,30,0.80) 100%)",
        }}
      ></div>

      {/* Content */}
      <div
        className="relative z-[4] w-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col lg:flex-row items-center lg:items-end self-stretch justify-end lg:justify-between gap-8 lg:gap-12 pt-28 md:pt-32 pb-28 md:pb-20"
        style={{
          opacity: visible ? 1 : 0,
          transition: "opacity 1s ease 0.3s",
        }}
      >
        {/* Left */}
        <div className="flex flex-col gap-8 max-w-2xl w-full items-center lg:items-start text-center lg:text-left">
          <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.05] md:leading-none tracking-tight">
            <span className="block font-black text-white">{t("hero.line1")}</span>
            <span className="block font-thin text-white/70 italic">{t("hero.line2")}</span>
            <span className="block font-black text-white">
              <span style={{ color: "#ed0578" }}>{t("hero.line3")}</span>
            </span>
          </h1>

          <div className="flex flex-col sm:flex-row gap-4">
            {/* Primario — relleno rosa */}
            <button
              onClick={() => handleNav("#contacto")}
              className="flex items-center justify-center gap-2 px-7 py-4 rounded-full text-white font-semibold text-sm hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: "#ed0578" }}
            >
              {t("hero.ctaPrimary")}
            </button>
            {/* Secundario — outline */}
            <button
              onClick={() => handleNav("#talentos")}
              className="flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-white/50 text-white font-medium text-sm hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("hero.ctaSecondary")}
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center z-[4] pointer-events-none">
        <div className="flex flex-col items-center gap-2 animate-bounce">
          <span className="text-white/40 text-xs uppercase tracking-widest text-center">{t("hero.scroll")}</span>
          <span className="w-5 h-5 flex items-center justify-center text-white/40">
            <i className="ri-arrow-down-line"></i>
          </span>
        </div>
      </div>
    </section>
  );
}